import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme, themes } from './ThemeProvider';
import { useAuthContext } from '../contexts/AuthContext';
import { 
  Zap, 
  Star, 
  TrendingUp, 
  Eye, 
  MessageCircle, 
  Heart,
  CheckCircle,
  Crown,
  Sparkles,
  ArrowRight,
  Info,
  ShoppingCart,
  Shield,
  X,
  Percent,
  Tag,
  History,
  ChevronDown,
  ChevronUp,
  Clock,
  CreditCard
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { BACKEND_CONFIG } from '../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Plan {
  _id: string;
  name: string;
  description: string;
  credits: number;
  price: number;
  features: string[];
  icon: string;
  isActive: boolean;
  badge?: string;
  color: string;
}

interface Coupon {
  code: string;
  discount: number;
  planId: string;
  marketingUserId: string;
  marketingUserName: string;
}

interface PremiumShopProps {
  onClose: () => void;
}

export default function PremiumShop({ onClose }: PremiumShopProps) {
  const { user, loadUser } = useAuthContext();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [verifyingCoupon, setVerifyingCoupon] = useState(false);
  const [showCouponField, setShowCouponField] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [premiumPlans, setPremiumPlans] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchPlans();
    fetchOrders();
    fetchPremiumPlans();
    loadRazorpayScript();
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/premium-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(data.data || []);
    } catch (error: any) {
      console.log('Plans not available from backend - using empty list');
      // Fail silently - backend might not be running
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/payments/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchPremiumPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/premium-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPremiumPlans(data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch premium plans:', error);
    }
  };

  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: any } = {
      Zap, Star, TrendingUp, Eye, MessageCircle, Heart, Crown, Sparkles
    };
    return icons[iconName] || Zap;
  };

  const handlePurchase = async (plan: Plan) => {
    if (processing) return;
    
    setProcessing(true);
    setSelectedPlan(plan);

    try {
      const token = localStorage.getItem('token');
      
      // Create order on backend
      const { data: orderData } = await axios.post(
        `${API_URL}/payments/create-order`,
        {
          planId: plan._id,
          amount: plan.price,
          credits: plan.credits
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const order = orderData.data;

      // Razorpay options
      const options = {
        key: order.razorpayKeyId, // Will be sent from backend
        amount: order.amount,
        currency: 'INR',
        name: 'SoulMate Matrimony',
        description: `${plan.name} - ${plan.credits} Credits`,
        order_id: order.orderId,
        handler: async function (response: any) {
          try {
            // Verify payment on backend
            const { data: verifyData } = await axios.post(
              `${API_URL}/payments/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                planId: plan._id,
                credits: plan.credits
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (verifyData.success) {
              toast.success(`🎉 ${plan.credits} credits added successfully!`);
              await loadUser(); // Reload user to get updated credits
              setSelectedPlan(null);
              
              // Show success details
              toast.success(`Your profile has been boosted!`, {
                description: `You now have ${verifyData.data.totalCredits} credits`
              });
            }
          } catch (error: any) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed. Contact support.');
          } finally {
            setProcessing(false);
          }
        },
        prefill: {
          name: `${user?.firstName} ${user?.lastName}`,
          email: user?.email,
          contact: user?.phone
        },
        notes: {
          userId: user?._id,
          planId: plan._id
        },
        theme: {
          color: '#ec4899'
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
            setSelectedPlan(null);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error: any) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const verifyCoupon = async () => {
    if (verifyingCoupon) return;
    setVerifyingCoupon(true);

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/coupons/verify`,
        {
          code: couponCode,
          planId: selectedPlan?._id
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (data.success) {
        setAppliedCoupon(data.data);
        toast.success(`Coupon applied successfully!`);
      } else {
        toast.error(data.message || 'Failed to apply coupon');
      }
    } catch (error: any) {
      console.error('Coupon verification error:', error);
      toast.error('Failed to apply coupon');
    } finally {
      setVerifyingCoupon(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin">
              <Crown className="size-6 text-pink-600" />
            </div>
            <p className="text-lg">Loading premium plans...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 z-50 overflow-y-auto">
      <div className="min-h-screen p-4 md:p-8">
        {/* Header */}
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl">
                <Crown className="size-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                  Premium Shop
                </h1>
                <p className="text-gray-600 mt-1">
                  Boost your profile and unlock premium features
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onClose}
              className="rounded-full"
            >
              <X className="size-6" />
            </Button>
          </div>

          {/* User Credits Banner */}
          {user && (
            <Card className="p-6 mb-8 bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 border-0 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Sparkles className="size-8" />
                  </div>
                  <div>
                    <p className="text-sm opacity-90">Your Credits</p>
                    <p className="text-3xl font-bold">{(user as any).credits || 0}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-90">Profile Boost Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    {(user as any).credits > 0 ? (
                      <>
                        <CheckCircle className="size-5" />
                        <span className="font-semibold">Active</span>
                      </>
                    ) : (
                      <>
                        <Info className="size-5" />
                        <span className="font-semibold">Inactive</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <Shield className="size-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Secure Payment via Razorpay</h3>
                <p className="text-sm text-blue-700">
                  All transactions are secured with industry-standard encryption. Your payment information is never stored on our servers.
                </p>
              </div>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {plans
              .filter(plan => plan.isActive)
              .map((plan) => {
                const IconComponent = getIconComponent(plan.icon);
                const isProcessing = processing && selectedPlan?._id === plan._id;
                
                return (
                  <Card 
                    key={plan._id}
                    className={`relative overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-1 ${
                      plan.badge ? 'border-2 border-pink-500' : ''
                    }`}
                  >
                    {plan.badge && (
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-gradient-to-r from-pink-500 to-purple-600 text-white border-0">
                          {plan.badge}
                        </Badge>
                      </div>
                    )}

                    <div className={`h-2 bg-gradient-to-r ${plan.color}`} />
                    
                    <div className="p-6">
                      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${plan.color} mb-4`}>
                        <IconComponent className="size-8 text-white" />
                      </div>

                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-gray-600 mb-4 min-h-[48px]">{plan.description}</p>

                      <div className="flex items-baseline gap-2 mb-6">
                        <span className="text-4xl font-bold text-gray-900">₹{plan.price}</span>
                        <span className="text-gray-500">/ {plan.credits} credits</span>
                      </div>

                      <div className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle className="size-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center gap-4">
                        <Button
                          onClick={() => handlePurchase(plan)}
                          disabled={isProcessing}
                          className={`w-full bg-gradient-to-r ${plan.color} text-white hover:opacity-90 transition-all`}
                        >
                          {isProcessing ? (
                            <>
                              <div className="animate-spin mr-2">
                                <Crown className="size-5" />
                              </div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <ShoppingCart className="size-5 mr-2" />
                              Buy Now
                              <ArrowRight className="size-5 ml-2" />
                            </>
                          )}
                        </Button>

                        <Button
                          onClick={() => {
                            setSelectedPlan(plan);
                            setShowCouponField(true);
                          }}
                          className="bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                        >
                          <Tag className="size-5 mr-2" />
                          Apply Coupon
                        </Button>
                      </div>

                      {showCouponField && selectedPlan?._id === plan._id && (
                        <div className="mt-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              placeholder="Enter coupon code"
                              className="border border-gray-300 rounded px-3 py-2 w-full"
                            />
                            <Button
                              onClick={verifyCoupon}
                              disabled={verifyingCoupon}
                              className="bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                            >
                              {verifyingCoupon ? (
                                <>
                                  <div className="animate-spin mr-2">
                                    <Crown className="size-5" />
                                  </div>
                                  Verifying...
                                </>
                              ) : (
                                <>
                                  <Percent className="size-5 mr-2" />
                                  Verify
                                </>
                              )}
                            </Button>
                          </div>

                          {appliedCoupon && appliedCoupon.planId === plan._id && (
                            <div className="mt-2 text-sm text-green-500">
                              Coupon applied! You get {appliedCoupon.discount}% off.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
          </div>

          {plans.filter(plan => plan.isActive).length === 0 && (
            <div className="text-center py-16">
              <Crown className="size-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Plans Available</h3>
              <p className="text-gray-600">Check back soon for premium plans!</p>
            </div>
          )}

          {/* How It Works */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-center">How Credits Work</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ShoppingCart className="size-8 text-pink-600" />
                </div>
                <h3 className="font-semibold mb-2">1. Purchase Credits</h3>
                <p className="text-sm text-gray-600">Select a plan and pay securely via Razorpay</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="size-8 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-2">2. Profile Boost</h3>
                <p className="text-sm text-gray-600">Your profile visibility increases automatically</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Eye className="size-8 text-indigo-600" />
                </div>
                <h3 className="font-semibold mb-2">3. More Views</h3>
                <p className="text-sm text-gray-600">Get shown to more potential matches</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart className="size-8 text-rose-600" />
                </div>
                <h3 className="font-semibold mb-2">4. Find Love</h3>
                <p className="text-sm text-gray-600">Increase your chances of finding your match</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}